import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, TextField, Button, Typography, Alert } from '@mui/material';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import { login, verifyPortalMfa } from '../slices/portalAuthSlice';
import { tokens } from '../../theme/theme';
import BrandLogo from '../../components/branding/BrandLogo';

export default function PortalLoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status, error, challengeToken } = useSelector((s) => s.portalAuth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = challengeToken
      ? await dispatch(verifyPortalMfa({ challengeToken, code }))
      : await dispatch(login({ email, password }));
    if ((login.fulfilled.match(result) && !result.payload.mfaRequired) || verifyPortalMfa.fulfilled.match(result)) navigate('/portal');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: tokens.brand.gradient, px: 2, position: 'relative', overflow: 'hidden',
        '&::before': { content: '""', position: 'absolute', inset: 0, background: 'radial-gradient(circle at 20% 15%, rgba(34,199,232,.18), transparent 34%)' }
      }}
    >
      <Paper sx={{ p: { xs: 3, sm: 4.5 }, width: '100%', maxWidth: 430, borderRadius: 3, position: 'relative', boxShadow: '0 28px 80px rgba(0,0,0,.38)' }}>
        <Box sx={{ mb: 3 }}>
          <BrandLogo sx={{ mb: 2.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VerifiedUserRoundedIcon sx={{ color: tokens.brand.main }} />
            <Typography variant="overline" sx={{ color: tokens.brand.main, fontSize: '0.66rem' }}>CyberGuard Client Security Portal</Typography>
          </Box>
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{challengeToken ? 'Verify your identity' : 'Secure client access'}</Typography>

        <Typography variant="body2" sx={{ color: tokens.text.secondary, mb: 3 }}>
          {challengeToken ? 'Enter the 6-digit code from your authenticator app.' : 'Sign in to view security reports, vulnerability findings and support tickets.'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Work email" type="email" size="small" value={email}
            onChange={(e) => setEmail(e.target.value)} required autoFocus
          />
          {!challengeToken ? <TextField
            label="Password" type="password" size="small" value={password}
            onChange={(e) => setPassword(e.target.value)} required
          /> : <TextField
            label="Authenticator code" size="small" value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} required
            inputProps={{ inputMode: 'numeric', maxLength: 6 }}
          />}
          <Button type="submit" variant="contained" size="large" disabled={status === 'loading'}>
            {status === 'loading' ? 'Signing in…' : 'Sign in'}
          </Button>
        </Box>

        <Typography variant="caption" sx={{ color: tokens.text.muted, display: 'block', mt: 2.5 }}>
          Authorised users only · Access is monitored and audited.
        </Typography>
      </Paper>
    </Box>
  );
}
